import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

export interface Notification {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  title: string
  message: string
  timestamp: Date
  read: boolean
}

export interface UIState {
  // Sidebar state
  sidebarCollapsed: boolean
  sidebarHovered: boolean
  sidebarOpen: boolean // For mobile sidebar toggle

  // Theme state (managed by shadcn theme system)
  theme: 'light' | 'dark'

  // Notifications
  notifications: Notification[]
  unreadNotifications: number

  // Loading states for different operations
  isPageLoading: boolean
  isSaving: boolean

  // Actions
  setSidebarCollapsed: (collapsed: boolean) => void
  setSidebarHovered: (hovered: boolean) => void
  setSidebarOpen: (open: boolean) => void // For mobile sidebar toggle
  setTheme: (theme: 'light' | 'dark') => void
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void
  markNotificationRead: (id: string) => void
  markAllNotificationsRead: () => void
  removeNotification: (id: string) => void
  clearAllNotifications: () => void
  setPageLoading: (loading: boolean) => void
  setSaving: (saving: boolean) => void
}

/**
 * UI state store for managing application-wide UI state
 * Includes sidebar, theme, notifications, and loading states
 */
export const useUIStore = create<UIState>()(
  devtools(
    (set) => ({
      // Initial state
      sidebarCollapsed: false,
      sidebarHovered: false,
      sidebarOpen: false, // Closed by default on mobile
      // Detect system preference on first load, default to light if can't detect
      theme: (() => {
        const saved = localStorage.getItem('ui-theme')
        let initialTheme: 'light' | 'dark' = 'light'
        
        if (saved && (saved === 'light' || saved === 'dark')) {
          initialTheme = saved as 'light' | 'dark'
        } else if (typeof window !== 'undefined') {
          // Detect system preference
          initialTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
        }
        
        // Apply theme immediately on initialization
        if (typeof window !== 'undefined') {
          const root = window.document.documentElement
          root.classList.remove('light', 'dark')
          root.classList.add(initialTheme)
        }
        
        return initialTheme
      })(),
      notifications: [],
      unreadNotifications: 0,
      isPageLoading: false,
      isSaving: false,

      // Sidebar actions
      setSidebarCollapsed: (collapsed: boolean) => {
        set({ sidebarCollapsed: collapsed }, false, 'ui/setSidebarCollapsed')
      },

      setSidebarHovered: (hovered: boolean) => {
        set({ sidebarHovered: hovered }, false, 'ui/setSidebarHovered')
      },

      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open }, false, 'ui/setSidebarOpen')
      },

      // Theme actions
      setTheme: (theme: 'light' | 'dark') => {
        set({ theme }, false, 'ui/setTheme')
        localStorage.setItem('ui-theme', theme)
        
        // Apply theme to document
        const root = window.document.documentElement
        root.classList.remove('light', 'dark')
        root.classList.add(theme)
      },

      // Notification actions
      addNotification: (notification) => {
        const newNotification: Notification = {
          ...notification,
          id: crypto.randomUUID(),
          timestamp: new Date(),
          read: false,
        }

        set(
          (state) => ({
            notifications: [newNotification, ...state.notifications],
            unreadNotifications: state.unreadNotifications + 1,
          }),
          false,
          'ui/addNotification'
        )
      },

      markNotificationRead: (id: string) => {
        set(
          (state) => ({
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
            unreadNotifications: Math.max(0, state.unreadNotifications - 1),
          }),
          false,
          'ui/markNotificationRead'
        )
      },

      markAllNotificationsRead: () => {
        set(
          (state) => ({
            notifications: state.notifications.map((n) => ({ ...n, read: true })),
            unreadNotifications: 0,
          }),
          false,
          'ui/markAllNotificationsRead'
        )
      },

      removeNotification: (id: string) => {
        set(
          (state) => {
            const notification = state.notifications.find((n) => n.id === id)
            const wasUnread = notification && !notification.read
            return {
              notifications: state.notifications.filter((n) => n.id !== id),
              unreadNotifications: wasUnread
                ? Math.max(0, state.unreadNotifications - 1)
                : state.unreadNotifications,
            }
          },
          false,
          'ui/removeNotification'
        )
      },

      clearAllNotifications: () => {
        set(
          { notifications: [], unreadNotifications: 0 },
          false,
          'ui/clearAllNotifications'
        )
      },

      // Loading actions
      setPageLoading: (loading: boolean) => {
        set({ isPageLoading: loading }, false, 'ui/setPageLoading')
      },

      setSaving: (saving: boolean) => {
        set({ isSaving: saving }, false, 'ui/setSaving')
      },
    }),
    {
      name: 'ui-store',
    }
  )
) 